import { createSelector, createSlice } from '@reduxjs/toolkit';

import isEqual from 'lodash/isEqual';

import { SliceNameEnum } from '@app/enums';
import {
    defaultMapData,
    defaultSearchCriteria,
    defaultSearchFilters,
    SearchState,
} from '@app/models';
import { fetchMapAccommodations } from '@app/state/api-thunks/map-accommodations.thunk';

import {
    initMapSearchFromUrlAction,
    resetSearchCriteriaAction,
    resetSearchFiltersAction,
    setFiltersVisibilityAction,
    setLocationDetailsAction,
    setMapSearchLoadingAction,
    setSelectedAccommodationAction,
    toggleFiltersVisibilityAction,
    updateSearchCriteriaAction,
    updateSearchFiltersAction,
} from './map-state.actions';

export const initialState: SearchState = {
    searchCriteria: defaultSearchCriteria,
    filters: defaultSearchFilters,
    isFiltersContentVisible: false,
    meta: {
        isLoading: false,
        hasSearchChanged: false,
        lastSearchTimestamp: null,
    },
    mapData: defaultMapData,
};

export const mapStateSlice = createSlice({
    name: SliceNameEnum.Map,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(initMapSearchFromUrlAction, (state, { payload }) => {
            const { searchCriteria, filters } = payload;

            state.searchCriteria = {
                ...state.searchCriteria,
                ...searchCriteria,
            };

            state.filters = {
                ...state.filters,
                ...filters,
            };

            state.meta.hasSearchChanged = false;
            state.meta.lastSearchTimestamp = Date.now();
        });

        builder.addCase(updateSearchCriteriaAction, (state, { payload }) => {
            const prevCriteria = state.searchCriteria;

            state.searchCriteria = {
                ...state.searchCriteria,
                ...payload.criteria,
            };

            state.meta.hasSearchChanged = !isEqual(prevCriteria, state.searchCriteria);
        });

        builder.addCase(updateSearchFiltersAction, (state, { payload }) => {
            const prevFilters = state.filters;

            state.filters = {
                ...state.filters,
                ...payload.filters,
            };

            state.meta.hasSearchChanged = !isEqual(prevFilters, state.filters);
        });

        builder.addCase(resetSearchCriteriaAction, state => {
            state.searchCriteria = defaultSearchCriteria;
            state.meta.hasSearchChanged = true;
        });

        builder.addCase(resetSearchFiltersAction, state => {
            state.filters = defaultSearchFilters;
            state.meta.hasSearchChanged = true;
        });

        builder.addCase(setMapSearchLoadingAction, (state, { payload }) => {
            state.meta.isLoading = payload;
            if (payload === false) {
                state.meta.lastSearchTimestamp = Date.now();
            }
        });

        builder.addCase(fetchMapAccommodations.pending, state => {
            state.mapData.isLoadingAccommodations = true;
            state.mapData.accommodationsError = null;
        });

        builder.addCase(fetchMapAccommodations.fulfilled, (state, { payload }) => {
            state.mapData.isLoadingAccommodations = false;
            state.mapData.accommodations = payload.accommodations;
            state.mapData.accommodationsError = null;
        });

        builder.addCase(fetchMapAccommodations.rejected, (state, { payload }) => {
            state.mapData.isLoadingAccommodations = false;
            state.mapData.accommodationsError =
                typeof payload === 'string' ? payload : 'Failed to fetch map accommodations';
        });

        builder.addCase(setLocationDetailsAction, (state, { payload }) => {
            state.mapData.locationDetails = payload.locationDetails;
        });

        builder.addCase(setSelectedAccommodationAction, (state, { payload }) => {
            state.mapData.selectedAccommodation = payload.accommodation;
        });

        builder.addCase(toggleFiltersVisibilityAction, state => {
            state.isFiltersContentVisible = !state.isFiltersContentVisible;
        });

        builder.addCase(setFiltersVisibilityAction, (state, { payload }) => {
            state.isFiltersContentVisible = payload.open;
        });
    },
});

const selectSelfState = (state: { [SliceNameEnum.Map]: SearchState }): SearchState => {
    return state[mapStateSlice.name] ?? initialState;
};

export const selectors = {
    selectSearchCriteria: createSelector(selectSelfState, state => state.searchCriteria),

    selectSearchFilters: createSelector(selectSelfState, state => state.filters),

    selectIsMapSearchLoading: createSelector(selectSelfState, state => state.meta.isLoading),

    selectHasMapSearchChanged: createSelector(
        selectSelfState,
        state => state.meta.hasSearchChanged,
    ),

    selectMapSearchLastTimestamp: createSelector(
        selectSelfState,
        state => state.meta.lastSearchTimestamp,
    ),

    selectMapAccommodations: createSelector(selectSelfState, state => state.mapData.accommodations),

    selectLocationDetails: createSelector(selectSelfState, state => state.mapData.locationDetails),

    selectIsLoadingAccommodations: createSelector(
        selectSelfState,
        state => state.mapData.isLoadingAccommodations,
    ),

    selectAccommodationsError: createSelector(
        selectSelfState,
        state => state.mapData.accommodationsError,
    ),

    selectSelectedAccommodation: createSelector(
        selectSelfState,
        state => state.mapData.selectedAccommodation,
    ),

    selectIsFiltersContentVisible: createSelector(
        selectSelfState,
        state => state.isFiltersContentVisible,
    ),
};

export default mapStateSlice.reducer;
